import { PART_FINDER_ACTIVITY_QUERY_KEY } from '@/hooks/api/part-finder-activity/query';
import { PART_FINDER_BATCH_QUERY_KEY, PART_FINDER_BATCHES_QUERY_KEY } from '@/hooks/api/part-finder-batches/query';
import { PART_FINDER_REPORTS_QUERY_KEY } from '@/hooks/api/part-finder-reports/query';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { useQueryClient } from '@tanstack/react-query';
import { BulkModal } from '../modals/BulkModal';

interface ActivityCancelItem {
  id: string;
  kind: 'batch' | 'report';
  targetId: string;
}

export const BulkCancelActivityModal = ({
  open,
  onClose,
  cancellableBatchIds,
  cancellableReportIds,
  onCancelComplete,
}: {
  open: boolean;
  onClose: () => void;
  cancellableBatchIds: string[];
  cancellableReportIds: string[];
  onCancelComplete: () => void;
}) => {
  const { getTenantId } = useTenants();
  const queryClient = useQueryClient();

  const items: ActivityCancelItem[] = [
    ...cancellableBatchIds.map(id => ({ id: `batch:${id}`, kind: 'batch' as const, targetId: id })),
    ...cancellableReportIds.map(id => ({ id: `report:${id}`, kind: 'report' as const, targetId: id })),
  ];

  const handleCancelItem = async (item: ActivityCancelItem) => {
    const tenantId = getTenantId();
    try {
      await (item.kind === 'batch'
        ? api.tenants.cancelPartFinderBatch(tenantId, item.targetId)
        : api.tenants.cancelPartFinderReport(tenantId, item.targetId));
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 400 || status === 404) return;
      throw error;
    }
  };

  const handleComplete = () => {
    queryClient.resetQueries({ queryKey: [PART_FINDER_ACTIVITY_QUERY_KEY], exact: false });
    queryClient.resetQueries({ queryKey: [PART_FINDER_REPORTS_QUERY_KEY], exact: false });
    queryClient.resetQueries({ queryKey: [PART_FINDER_BATCHES_QUERY_KEY], exact: false });
    queryClient.resetQueries({ queryKey: [PART_FINDER_BATCH_QUERY_KEY], exact: false });
    onCancelComplete();
  };

  return (
    <BulkModal
      open={open}
      onClose={onClose}
      selectedItems={items}
      onComplete={handleComplete}
      performOperation={handleCancelItem}
      buttonColor='warning'
      labels={{
        title: 'Cancel items',
        action: 'cancel',
        actionPastTense: 'cancelled',
        actionPresent: 'Cancelling',
        confirmButton: 'Cancel all',
        cancelButton: 'Keep running',
        itemSingular: 'item',
        itemPlural: 'items',
        description: count =>
          `${count} ${count === 1 ? 'item' : 'items'} will be cancelled. Cancelling a batch also cancels its pending parts; in-flight searches finish on their own.`,
      }}
    />
  );
};
